
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Lightbulb, Shield, Thermometer, Music, Droplets, Zap, Clock, Smartphone, Heart } from 'lucide-react';

const WhatIsDomotics = () => {
  const benefits = [
  {
    icon: <Heart className="w-8 h-8" data-id="4a6tzz6ds" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Alegra tu Vida',
    description: 'Despierta con tu café listo, música favorita sonando y la casa perfectamente climatizada.',
    color: 'from-pink-500 to-red-500'
  },
  {
    icon: <Shield className="w-8 h-8" data-id="b3ycyn1zh" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Seguridad Total',
    description: 'Monitorea y controla tu hogar desde cualquier lugar del mundo. Nunca más te preocupes por si dejaste algo encendido.',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    icon: <Zap className="w-8 h-8" data-id="8a9n9qzdp" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Ahorro Inteligente',
    description: 'Reduce hasta 40% tu consumo energético con automatizaciones inteligentes que optimizan cada dispositivo.',
    color: 'from-yellow-500 to-orange-500'
  },
  {
    icon: <Clock className="w-8 h-8" data-id="0tti9xeav" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Simplifica tu Día',
    description: 'Una sola app controla luces, música, temperatura, seguridad y más. Todo sincronizado con tu rutina diaria.',
    color: 'from-green-500 to-teal-500'
  }];


  const examples = [
  {
    icon: <Lightbulb className="w-6 h-6" data-id="czzvk7trq" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Iluminación Inteligente',
    description: 'Luces que se ajustan automáticamente según la hora del día y tus actividades.'
  },
  {
    icon: <Music className="w-6 h-6" data-id="vimxh51fu" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Audio Multizona',
    description: 'Música Sonos sincronizada en toda la casa o diferente en cada habitación.'
  },
  {
    icon: <Thermometer className="w-6 h-6" data-id="x0ayn9o94" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Clima Perfecto',
    description: 'Temperatura ideal en cada espacio, ahorrando energía automáticamente.'
  },
  {
    icon: <Droplets className="w-6 h-6" data-id="7g7ihw5g5" data-path="src/components/WhatIsDomotics.tsx" />,
    title: 'Riego Inteligente',
    description: 'Jardines siempre perfectos con riego automático basado en el clima.'
  }];


  return (
    <section id="domotica" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" data-id="a3wj8xzxd" data-path="src/components/WhatIsDomotics.tsx">
      <div className="container mx-auto px-4" data-id="z4fwdmvar" data-path="src/components/WhatIsDomotics.tsx">
        {/* Header */}
        <div className="text-center mb-16" data-id="eanl22l2m" data-path="src/components/WhatIsDomotics.tsx">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-id="p8r4l6fhl" data-path="src/components/WhatIsDomotics.tsx">
            ¿Qué es la
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="mx4de7k1q" data-path="src/components/WhatIsDomotics.tsx"> Automatización de Casas?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="yj2n0ipmw" data-path="src/components/WhatIsDomotics.tsx">
            La domótica es la integración de tecnología en tu hogar para crear un ambiente inteligente que 
            <span className="font-semibold text-blue-600" data-id="iu4y7tnm3" data-path="src/components/WhatIsDomotics.tsx"> se adapta a ti</span>. Imagina un hogar que piensa, 
            actúa y mejora tu vida diaria de manera automática.
          </p>
        </div>

        {/* Main Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="teh44o21n" data-path="src/components/WhatIsDomotics.tsx">
          {benefits.map((benefit, index) =>
          <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden" data-id="1bs8bjjnt" data-path="src/components/WhatIsDomotics.tsx">
              <CardContent className="p-6" data-id="3tvghisv7" data-path="src/components/WhatIsDomotics.tsx">
                <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${benefit.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300`} data-id="8r850c42v" data-path="src/components/WhatIsDomotics.tsx">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="fkm1bl4pp" data-path="src/components/WhatIsDomotics.tsx">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed" data-id="hifbjn9k3" data-path="src/components/WhatIsDomotics.tsx">{benefit.description}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Examples Section */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-xl" data-id="0r9uvv6lu" data-path="src/components/WhatIsDomotics.tsx">
          <div className="text-center mb-12" data-id="ysjes7d9r" data-path="src/components/WhatIsDomotics.tsx">
            <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="w6n3fqgkw" data-path="src/components/WhatIsDomotics.tsx">
              Ejemplos de Automatización que 
              <span className="text-blue-600" data-id="y3jo463mt" data-path="src/components/WhatIsDomotics.tsx"> Transforman tu Vida</span>
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="rce4oi937" data-path="src/components/WhatIsDomotics.tsx">
              Descubre cómo la tecnología puede hacer tu día a día más cómodo, seguro y eficiente.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="94ecpuclx" data-path="src/components/WhatIsDomotics.tsx">
            {examples.map((example, index) =>
            <div key={index} className="text-center group" data-id="fhspqei2s" data-path="src/components/WhatIsDomotics.tsx">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300" data-id="mznsixqcc" data-path="src/components/WhatIsDomotics.tsx">
                  {example.icon}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="yacabjfvq" data-path="src/components/WhatIsDomotics.tsx">{example.title}</h4>
                <p className="text-gray-600 text-sm" data-id="00lzyfbtf" data-path="src/components/WhatIsDomotics.tsx">{example.description}</p>
              </div>
            )}
          </div>

          {/* Scenario Examples */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8" data-id="j4xciptw1" data-path="src/components/WhatIsDomotics.tsx">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="a0ys72mxk" data-path="src/components/WhatIsDomotics.tsx">
              🌅 Un Día en tu Hogar Inteligente
            </h4>
            
            <div className="grid md:grid-cols-3 gap-6" data-id="z6v08yiop" data-path="src/components/WhatIsDomotics.tsx">
              <div className="text-center" data-id="jeybiu2tv" data-path="src/components/WhatIsDomotics.tsx">
                <div className="bg-yellow-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center" data-id="6950rjbmp" data-path="src/components/WhatIsDomotics.tsx">
                  <span className="text-2xl" data-id="zh1myph8y" data-path="src/components/WhatIsDomotics.tsx">🌅</span>
                </div>
                <h5 className="font-semibold text-gray-900 mb-2" data-id="wri85u7fk" data-path="src/components/WhatIsDomotics.tsx">Buenos Días</h5>
                <p className="text-sm text-gray-600" data-id="gbq0d6l97" data-path="src/components/WhatIsDomotics.tsx">
                  Las luces se encienden gradualmente, tu música favorita comienza a sonar y el café está listo.
                </p>
              </div>
              
              <div className="text-center" data-id="ovxin4eto" data-path="src/components/WhatIsDomotics.tsx">
                <div className="bg-blue-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center" data-id="7diew66to" data-path="src/components/WhatIsDomotics.tsx">
                  <span className="text-2xl" data-id="cueaw86xl" data-path="src/components/WhatIsDomotics.tsx">🏠</span>
                </div>
                <h5 className="font-semibold text-gray-900 mb-2" data-id="10zz0c414" data-path="src/components/WhatIsDomotics.tsx">Fuera de Casa</h5>
                <p className="text-sm text-gray-600" data-id="nfy6tk4s9" data-path="src/components/WhatIsDomotics.tsx">
                  El sistema activa la seguridad, ajusta la temperatura y vigila tu hogar automáticamente.
                </p>
              </div>
              
              <div className="text-center" data-id="hll3pbpf0" data-path="src/components/WhatIsDomotics.tsx">
                <div className="bg-purple-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center" data-id="wvtxogkij" data-path="src/components/WhatIsDomotics.tsx">
                  <span className="text-2xl" data-id="o7th08e0l" data-path="src/components/WhatIsDomotics.tsx">🌙</span>
                </div>
                <h5 className="font-semibold text-gray-900 mb-2" data-id="ba80lr6zo" data-path="src/components/WhatIsDomotics.tsx">Buenas Noches</h5>
                <p className="text-sm text-gray-600" data-id="e1qfkk2gm" data-path="src/components/WhatIsDomotics.tsx">
                  Todo se apaga automáticamente, las puertas se aseguran y el sistema nocturno se activa.
                </p>
              </div>
            </div>
          </div>

          {/* Voice Control Section */}
          <div className="mt-12 text-center" data-id="5rpk1b926" data-path="src/components/WhatIsDomotics.tsx">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-8 text-white" data-id="t0rj9kyka" data-path="src/components/WhatIsDomotics.tsx">
              <h4 className="text-2xl font-bold mb-4" data-id="2sc1ni9wr" data-path="src/components/WhatIsDomotics.tsx">
                🗣️ Control por Voz Natural
              </h4>
              <p className="text-lg mb-6 opacity-90" data-id="zkd1kg7an" data-path="src/components/WhatIsDomotics.tsx">
                "Ok Google, modo película" • "Alexa, buenas noches" • "Enciende las luces de la sala"
              </p>
              <div className="flex justify-center items-center space-x-6 opacity-75" data-id="nvvhk3xdt" data-path="src/components/WhatIsDomotics.tsx">
                <Smartphone className="w-8 h-8" data-id="fpi2nmccb" data-path="src/components/WhatIsDomotics.tsx" />
                <span className="text-sm" data-id="x3muojcjf" data-path="src/components/WhatIsDomotics.tsx">+</span>
                <span className="text-sm font-medium" data-id="503bz6bzc" data-path="src/components/WhatIsDomotics.tsx">Google Home</span>
                <span className="text-sm" data-id="t9r7bnusc" data-path="src/components/WhatIsDomotics.tsx">+</span>
                <span className="text-sm font-medium" data-id="rtggwg4w7" data-path="src/components/WhatIsDomotics.tsx">Alexa</span>
                <span className="text-sm" data-id="tx8ulay8j" data-path="src/components/WhatIsDomotics.tsx">+</span>
                <span className="text-sm font-medium" data-id="hyocr2en9" data-path="src/components/WhatIsDomotics.tsx">Siri</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default WhatIsDomotics;